import React from "react";
import moment from "moment";
import { partidaDateToLocal } from "../../utils";

const DIA = 24 * 60 * 60 * 1000;
const REAL = "#89d405";

// Same colors as the obra Gantt: the bar turns orange / red when real is 5% /
// 10% behind programado; the filled part is the real progress.
const colorBarra = (realPct, programadoPct) => {
  const diff = programadoPct - realPct;
  if (diff > 10) return "#dc3545";
  if (diff >= 5) return "#ff8008";
  return REAL;
};

/**
 * Report Gantt in plain HTML so it prints sharp on A4 (no zoom): one row per
 * partida with its bar, its name next to the bar and, below the bar, the
 * brief summary of its comments in the period (`resumenes[idPartida]`).
 */
const ReporteGanttPartidas = ({ partidas, resumenes }) => {
  const filas = (Array.isArray(partidas) ? partidas : [])
    .filter((p) => p.fecha_inicio && p.fecha_fin)
    .map((p) => ({
      ...p,
      inicio: partidaDateToLocal(p.fecha_inicio),
      fin: partidaDateToLocal(p.fecha_fin),
    }));
  if (filas.length === 0) {
    return <p className="text-muted">No hay partidas con fechas asignadas.</p>;
  }

  const desde = moment(Math.min(...filas.map((f) => f.inicio))).startOf("month");
  const hasta = moment(Math.max(...filas.map((f) => f.fin))).endOf("month");
  const total = hasta.valueOf() - desde.valueOf() + DIA;
  const pos = (fecha) => ((moment(fecha).valueOf() - desde.valueOf()) / total) * 100;

  const meses = [];
  for (let m = moment(desde); m.isBefore(hasta); m.add(1, "month")) {
    const inicio = pos(m);
    const fin = pos(moment(m).endOf("month")) + (DIA / total) * 100;
    meses.push({ key: m.format("YYYY-MM"), label: m.format("MMM YY"), inicio, ancho: fin - inicio });
  }
  const hoy = pos(new Date());
  const resumenDe = (p) => (resumenes ? resumenes[String(p.idPartida)] : null);

  return (
    <div className="reporte-gantt" style={{ fontSize: 11 }}>
      {/* Month header and grid */}
      <div style={{ position: "relative", height: 22, borderBottom: "1px solid #dee2e6" }}>
        {meses.map((mes) => (
          <div
            key={mes.key}
            style={{
              position: "absolute",
              left: `${mes.inicio}%`,
              width: `${mes.ancho}%`,
              top: 2,
              textAlign: "center",
              color: "#6c757d",
              textTransform: "capitalize",
            }}
          >
            {mes.label}
          </div>
        ))}
      </div>
      <div style={{ position: "relative" }}>
        {meses.map((mes) => (
          <div
            key={`linea-${mes.key}`}
            style={{
              position: "absolute",
              left: `${mes.inicio}%`,
              top: 0,
              bottom: 0,
              borderLeft: "1px solid #eee",
            }}
          />
        ))}
        {hoy > 0 && hoy < 100 && (
          <div
            style={{
              position: "absolute",
              left: `${hoy}%`,
              top: 0,
              bottom: 0,
              borderLeft: "2px dashed #f0c36d",
            }}
            title="Hoy"
          />
        )}
        {filas.map((p, index) => {
          const realPct = Math.round(Number(p.real) * 100) || 0;
          const programadoPct = Math.round(Number(p.programado) * 100) || 0;
          const inicio = pos(p.inicio);
          const ancho = Math.max(pos(p.fin) + (DIA / total) * 100 - inicio, 0.6);
          const fin = inicio + ancho;
          // Name after the bar, before it, or inside a long bar that leaves no
          // room on either side.
          const etiqueta = fin < 62 ? "derecha" : inicio >= 35 ? "izquierda" : "dentro";
          const resumen = resumenDe(p);
          // The summary starts under the bar, moved left when the bar is
          // near the end so it keeps room to read.
          const resumenInicio = Math.min(inicio, 55);
          return (
            <div
              key={p.idPartida}
              style={{
                position: "relative",
                padding: "4px 0",
                background: index % 2 ? "#fafafa" : "transparent",
                pageBreakInside: "avoid",
                breakInside: "avoid",
              }}
            >
              <div style={{ position: "relative", height: 16 }}>
                <div
                  style={{
                    position: "absolute",
                    left: `${inicio}%`,
                    width: `${ancho}%`,
                    height: 16,
                    borderRadius: 3,
                    background: colorBarra(realPct, programadoPct),
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(realPct, 100)}%`,
                      height: "100%",
                      background: REAL,
                    }}
                  />
                </div>
                <div
                  style={{
                    position: "absolute",
                    whiteSpace: "nowrap",
                    top: 1,
                    ...(etiqueta === "derecha"
                      ? { left: `calc(${fin}% + 4px)` }
                      : etiqueta === "izquierda"
                      ? { right: `calc(${100 - inicio}% + 4px)` }
                      : {
                          left: `calc(${inicio}% + 6px)`,
                          maxWidth: `calc(${ancho}% - 12px)`,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "#fff",
                          textShadow: "0 0 2px rgba(0,0,0,.45)",
                        }),
                  }}
                >
                  <span style={{ fontWeight: 700 }}>{p.nombre}</span>{" "}
                  <span style={{ color: etiqueta === "dentro" ? "#fff" : "#6c757d" }}>
                    ({realPct}% / {programadoPct}%)
                  </span>
                </div>
              </div>
              {resumen && (
                <div
                  className="reporte-gantt-resumen"
                  style={{
                    color: "var(--primary)",
                    fontWeight: 700,
                    marginLeft: `${resumenInicio}%`,
                    marginTop: 2,
                    fontSize: 10,
                    lineHeight: 1.3,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {resumen}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          marginTop: 8,
          color: "#6c757d",
          fontSize: 11,
        }}
      >
        {[
          ["Avance real / a tiempo", REAL],
          ["Retraso 5-10%", "#ff8008"],
          ["Retraso >10%", "#dc3545"],
        ].map(([label, color]) => (
          <span key={label}>
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                marginRight: 4,
                background: color,
              }}
            />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default ReporteGanttPartidas;
